import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import Menu from "../../comum/Menu/menu.jsx";
import Footer from "../../comum/Footer";
import Swal from 'sweetalert2';

// Reutilizamos o LoadingModal
function LoadingModal({ show, message }) {
    if (!show) return null;
    return (
        <div className="loading-modal-overlay">
            <div className="loading-modal-content">
                <div
                    className="spinner-border text-primary"
                    role="status"
                    style={{ width: "3rem", height: "3rem" }}
                >
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div style={{ marginTop: 15, fontSize: 18 }}>{message}</div>
            </div>
            <style>{`
        .loading-modal-overlay {
            position: fixed;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0,0,0,0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1050;
        }
        .loading-modal-content {
            background: white;
            padding: 20px 40px;
            border-radius: 8px;
            display: flex;
            flex-direction: column;
            align-items: center;
            box-shadow: 0 0 10px rgba(0,0,0,0.25);
        }
        .conteudo-principal {
            padding-top: 100px;
        }
      `}</style>
        </div>
    );
}

// Função para calcular a distância entre duas coordenadas (Fórmula de Haversine)
function calcularDistancia(lat1, lon1, lat2, lon2) {
    const R = 6371; // Raio da Terra em km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance;
}

function FazerChecklist() {
    const [lojas, setLojas] = useState([]);
    const [lojaSelecionada, setLojaSelecionada] = useState("");
    const [itensChecklist, setItensChecklist] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const navigate = useNavigate();
    const chapa = localStorage.getItem("chapa");
    const permiteCheckList= localStorage.getItem("CHECKLIST");

    // Hook para carregar a lista de lojas e tentar a geolocalização
    useEffect(() => {
        if (!chapa) {
            navigate("/arbor/login");
            return;
        }
        else if (permiteCheckList !== "S") {
                    Swal.fire({ icon: 'error', title: 'Acesso Negado', text: 'Não é permitido acessar o CheckList.', confirmButtonText: 'OK' })
                        .then(() => navigate("/arbor/home"));
                    }
        
        const carregarLojasEGeolocalizar = async () => {
            setLoading(true);
            setLoadingMessage("Buscando sua localização...");

            try {
                // 1. Obter a localização atual
                const posicao = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                        enableHighAccuracy: true,
                        timeout: 5000,
                        maximumAge: 0
                    });
                });
                const { latitude, longitude } = posicao.coords;

                // 2. Carregar a lista completa de lojas
                setLoadingMessage("Carregando lista de lojas...");
                const res = await api.get("lojas/localizacao");
                const lojasPermitidas = res.data.filter(l => l.cod_emp < 50 && l.cod_emp !== 1 && l.cod_emp !== 23);
                setLojas(lojasPermitidas);
                
                // 3. Encontrar a loja mais próxima
                if (lojasPermitidas.length > 0) {
                    let lojaMaisProxima = null;
                    let menorDistancia = Infinity;

                    lojasPermitidas.forEach(loja => {
                        if (loja.latitude && loja.longitude) {
                            const distancia = calcularDistancia(latitude, longitude, loja.latitude, loja.longitude);
                            if (distancia < menorDistancia) {
                                menorDistancia = distancia;
                                lojaMaisProxima = loja;
                            }
                        }
                    });

                    if (lojaMaisProxima) {
                        Swal.fire({
                            title: 'Loja Encontrada!',
                            text: `Parece que você está na Loja ${String(lojaMaisProxima.cod_emp).padStart(2, '0')} - ${lojaMaisProxima.razao}.`,
                            icon: 'info',
                            confirmButtonText: 'Continuar'
                        });
                        setLojaSelecionada(String(lojaMaisProxima.cod_emp));
                    } else {
                        Swal.fire('Aviso', 'Não foi possível encontrar uma loja próxima com as coordenadas disponíveis. Por favor, selecione manualmente.', 'warning');
                    }
                }

            } catch (error) {
                console.error("Erro de geolocalização ou ao carregar dados:", error);
                if (error.code === 1) { // PERMISSION_DENIED
                    Swal.fire('Aviso', 'Permissão de geolocalização negada. Por favor, selecione a loja manualmente.', 'warning');
                } else {
                    Swal.fire('Erro', 'Não foi possível detectar sua localização ou carregar as lojas. Por favor, selecione manualmente.', 'error');
                }
                // Carregar as lojas de qualquer forma para seleção manual
                try {
                    const res = await api.get("lojas/localizacao");
                    const lojasPermitidas = res.data.filter(l => l.cod_emp < 50 && l.cod_emp !== 1 && l.cod_emp !== 23);
                    setLojas(lojasPermitidas);
                } catch (e) {
                    console.error("Erro ao carregar lojas após falha na geolocalização:", e);
                }
            } finally {
                setLoading(false);
            }
        };

        carregarLojasEGeolocalizar();
    }, [chapa, navigate]);

    // Hook para carregar os itens de checklist da loja selecionada
    useEffect(() => {
        const carregarItens = async () => {
            if (!lojaSelecionada) {
                setItensChecklist([]);
                return;
            }
            try {
                setLoading(true);
                setLoadingMessage("Carregando itens de checklist...");
                const res = await api.get(`/checklist/loja/${lojaSelecionada}/itens`);
                const itensComEstado = res.data.map(item => ({
                    ...item,
                    status: '', // 'OK' ou 'NaoOK'
                    comentario: ''
                }));
                const itensAgrupados = itensComEstado.reduce((acc, item) => {
                    const tipo = item.tipo_equipamento || 'Outros';
                    if (!acc[tipo]) {
                        acc[tipo] = [];
                    }
                    acc[tipo].push(item);
                    return acc;
                }, {});
                setItensChecklist(itensAgrupados);
            } catch (error) {
                console.error("Erro ao carregar itens da loja:", error);
                const errorMessage = error.response?.data?.erro || "Não foi possível carregar os itens associados a esta loja.";
                Swal.fire('Erro', errorMessage, 'error');
                setItensChecklist([]);
            } finally {
                setLoading(false);
            }
        };
        carregarItens();
    }, [lojaSelecionada]);

    // Lida com a mudança de status/comentário de um item
    const handleItemChange = (itemId, campo, valor) => {
        setItensChecklist(prevItens => {
            const novosItens = { ...prevItens };
            for (const tipo in novosItens) {
                const itemIndex = novosItens[tipo].findIndex(item => item.id_item === itemId);
                if (itemIndex > -1) {
                    novosItens[tipo][itemIndex] = { ...novosItens[tipo][itemIndex], [campo]: valor };
                    break;
                }
            }
            return novosItens;
        });
    };

    // Lida com o envio do checklist
    const handleFinalizarChecklist = async () => {
        if (!lojaSelecionada) {
            Swal.fire('Aviso', 'Selecione uma loja para finalizar o checklist.', 'warning');
            return;
        }

        const todosItens = Object.values(itensChecklist).flat();
        const itensNaoAvaliados = todosItens.filter(item => !item.status);

        if (itensNaoAvaliados.length > 0) {
            Swal.fire('Aviso', 'Todos os itens devem ser avaliados.', 'warning');
            return;
        }

        try {
            setLoading(true);
            setLoadingMessage("Finalizando checklist...");
            
            const now = new Date();
            const dataExecucao = now.toISOString().slice(0, 10);
            const horaExecucao = now.toTimeString().slice(0, 5);

            const payload = {
                idLoja: parseInt(lojaSelecionada),
                idUsuario: chapa,
                dataExecucao: dataExecucao,
                horaExecucao: horaExecucao,
                itens: todosItens.map(item => ({
                    idItem: item.id_item,
                    status: item.status,
                    comentario: item.comentario
                }))
            };
            
            const res = await api.post('/checklist/finalizar', payload);
            
            Swal.fire('Sucesso!', res.data.mensagem, 'success');
            setLojaSelecionada(""); 
            setItensChecklist([]);
            
        } catch (error) {
            console.error("Erro ao finalizar checklist:", error);
            const errorMessage = error.response?.data?.erro || "Não foi possível finalizar o checklist.";
            Swal.fire('Erro', errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="d-flex flex-column min-vh-100">
            <Menu modulo="ARBOR" />
            <div className="container-fluid flex-grow-1 conteudo-principal">
                <div className="row justify-content-center">
                    <div className="col-lg-8">
                        <h4 className="mt-4">Fazer Checklist</h4>
                        <div className="mb-3">
                            <label className="form-label">Selecione a Loja</label>
                            <select
                                className="form-select"
                                value={lojaSelecionada}
                                onChange={e => setLojaSelecionada(e.target.value)}
                            >
                                <option value="">--- Selecione ---</option>
                                {lojas.map(l => (
                                    <option key={l.cod_emp} value={l.cod_emp}>
                                        Loja {String(l.cod_emp).padStart(2, '0')} - {l.razao}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {lojaSelecionada && Object.keys(itensChecklist).length > 0 && (
                            <div className="checklist-container">
                                {Object.entries(itensChecklist).map(([tipo, itens]) => (
                                    <div key={tipo} className="mb-4">
                                        <h5 className="border-bottom pb-2 mt-3">{tipo}</h5>
                                        {itens.map(item => (
                                            <div key={item.id_item} className="mb-3 p-3 border rounded">
                                                <h6>{item.descricao}</h6>
                                                <div className="form-check form-check-inline">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name={`status-${item.id_item}`}
                                                        value="S"
                                                        checked={item.status === 'S'}
                                                        onChange={() => handleItemChange(item.id_item, 'status', 'S')}
                                                    />
                                                    <label className="form-check-label">Tudo Certo 👍🏻</label>
                                                </div>
                                                <div className="form-check form-check-inline">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name={`status-${item.id_item}`}
                                                        value="E"
                                                        checked={item.status === 'E'}
                                                        onChange={() => handleItemChange(item.id_item, 'status', 'E')}
                                                    />
                                                    <label className="form-check-label">Problema encontrado 👎🏻</label>
                                                </div>
                                                {item.status === 'E' && (
                                                    <div className="mt-2">
                                                        <textarea
                                                            className="form-control"
                                                            placeholder="Detalhes do problema"
                                                            value={item.comentario}
                                                            onChange={e => handleItemChange(item.id_item, 'comentario', e.target.value)}
                                                            rows="2"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                                <div className="d-grid gap-2">
                                    <button
                                        className="btn btn-success btn-lg mt-3"
                                        onClick={handleFinalizarChecklist}
                                        disabled={loading}
                                    >
                                        Finalizar Checklist
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        {!lojaSelecionada && <div className="text-center text-muted py-5">Selecione uma loja para começar o checklist.</div>}
                        {lojaSelecionada && Object.keys(itensChecklist).length === 0 && !loading && (
                            <div className="text-center text-muted py-5">Nenhum item de checklist associado a esta loja.</div>
                        )}
                    </div>
                </div>
            </div>
            <LoadingModal show={loading} message={loadingMessage} />
            <Footer />
        </div>
    );
}

export default FazerChecklist;